import { MockedEndpoint } from './demo.model';

export const initEndpoints = (): MockedEndpoint[] => [
  // get available apps for global header
  {
    endpoint: /users\/v\d+\/users\/me\/apps$/,
    json: `/assets/demo/me.apps.json`,
  },
  // get logged in user data (me)
  {
    endpoint: /users\/v1\/users\/me/,
    json: `/assets/demo/me.json`,
  },
  // get remote config
  {
    endpoint: /remote-config\/v\d+\/fetch/,
    json: `/assets/demo/remote-config.json`,
  },
  // get units for settings
  {
    endpoint: /vitoguide\/settings\/v\d+\/units/,
    json: `/assets/demo/units.json`,
  },
  // installation panels order
  {
    endpoint: /vitoguide\/settings\/v\d+\/installationPanelsOrder\d+/,
    json: `/assets/demo/installationPanelsOrder.json`,
  },
  // installation information popup settings
  {
    endpoint: /vitoguide\/settings\/v\d+\/InformationPopupSettings/,
    json: `/assets/demo/informationPopupSettings.json`,
  },
  // get email notifications for settings
  {
    endpoint: new RegExp('iot/v\\d+/notifications/notifications/mail/subscription'),
    json: `/assets/demo/notifications.subscription.json`,
  },
  // get documents
  {
    endpoint: /vitoguide\/user-management\/registration\/privacy/,
    json: `/assets/demo/privacy.json`,
  },
  // get maintenane messages
  {
    endpoint: /status\/v\d+\/maintenances/,
    json: `/assets/demo/maintenances.json`,
  },
  // get info about app
  {
    endpoint: /status\/v\d+\/apps\/\w+/,
    json: `/assets/demo/apps.json`,
  },
  // get invitation owner details
  {
    endpoint: /iot\/v\d+\/user-management\/invitations\/[\W\w]+\/ownerDetails/,
    json: `/assets/demo/invitations.owner-details.json`,
  },
  // get invitation installation details
  {
    endpoint: /iot\/v\d+\/user-management\/invitations\/[\W\w]+\/installationDetails/,
    json: `/assets/demo/invitations.installation-details.json`,
  },
  // get invitations (incoming & pending)
  {
    endpoint: /iot\/v\d+\/user-management\/invitations\?[\W\w]+/,
    json: `/assets/demo/invitations.json`,
  },
  // get all installations
  {
    endpoint: /vitoguide\/installations\/v2\/all-gateways/,
    json: `/assets/demo/installations.all.json`,
  },
  // get legal documents GB
  {
    endpoint: /legal\/v\d+\/apps\/81bae46d-30c8-4b6e-bb40-de5844aebf65\/documents\?country=GB/,
    json: `/assets/demo/documents.GB.json`,
  },
  // get legal documents DE
  {
    endpoint: /legal\/v\d+\/apps\/81bae46d-30c8-4b6e-bb40-de5844aebf65\/documents\?country=DE/,
    json: `/assets/demo/documents.DE.json`,
  },
  {
    endpoint: /legal\/v\d+\/apps\/81bae46d-30c8-4b6e-bb40-de5844aebf65\/documents\?country=\w+/,
    json: `/assets/demo/documents.DE.json`,
  },
  // get updated legal documents
  {
    endpoint: /legal\/v\d+\/apps\/81bae46d-30c8-4b6e-bb40-de5844aebf65\/documents\/updated/,
    json: `/assets/demo/updated-documents.json`,
  },
  // Remote Service Recommendations All
  {
    endpoint: new RegExp(`iot/v\\d+/analytics/remoteService/recommendations`),
    json: `/assets/demo/permament-inspection/recommendations.json`,
  },
  // company for fetching vkorg
  {
    endpoint: new RegExp(`company\/v3\/companies\/\\d+`),
    json: `/assets/demo/company/companies.json`,
  },
  // Phraseapp translations
  {
    endpoint: new RegExp(`translations/v2/phraseapp-proxy/projects/\\w+/locales/\\w+/download`),
    json: `/assets/demo/translations.json`,
  },
  // get mail subscriptions / settings
  {
    endpoint: /iot\/v1\/mails\/subscriptions/,
    json: `/assets/demo/mail-subscriptions.json`,
  },
];

export const installationEndpoints = (
  deviceName: string,
  installationId: number,
  deviceId: string,
  deviceSerial: string,
  gatewaySerial: string,
  srcDeviceList: string[] = []
): MockedEndpoint[] => {
  const endpoint = [
    ...getEndpointsForSrcDevices(srcDeviceList, deviceName, installationId, gatewaySerial),
    // get basic installation details
    {
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations/${installationId}\\?includeGateways=true`
      ),
      json: `/assets/demo/${deviceName}/installations.json`,
    },
    // get installation access list
    {
      endpoint: new RegExp(`iot/v\\d+/equipment/installations/${installationId}/accessList$`),
      json: `/assets/demo/${deviceName}/installations.access-list.json`,
    },
    // get installation invitations
    {
      endpoint: new RegExp(`iot/v1/user-management/(invitations|invitations[\\W\\w]+)`),
      json: `/assets/demo/${deviceName}/invitations.json`,
    },
    // get installation devices list
    {
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations/${installationId}/gateways/${gatewaySerial}/devices$`
      ),
      json: `/assets/demo/${deviceName}/installations.devices.json`,
    },
    // get installation operating modes features for overview
    {
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations/${installationId}/gateways/${gatewaySerial}/devices/${deviceId}/(features|features[\\W\\w]+)`
      ),
      json: `/assets/demo/${deviceName}/installations.devices.${deviceId}.features.json`,
    },
    // get all installation device gateway features
    {
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations/${installationId}/gateways/${gatewaySerial}/devices/gateway/(features|features[\\W\\w]+)`
      ),
      json: `/assets/demo/${deviceName}/installations.devices.gateway.features.json`,
    },
    // get all installation device features
    {
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations/${installationId}/gateways/${gatewaySerial}/devices/${deviceId}/(features|features[\\W\\w]+)`
      ),
      json: `/assets/demo/${deviceName}/installations.devices.${deviceId}.features.json`,
    },
    // get all installation gateway features
    {
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations/${installationId}/gateways/${gatewaySerial}/(features|features[\\W\\w]+)`
      ),
      json: `/assets/demo/${deviceName}/installations.gateways.features.json`,
    },
    // get gateway details
    {
      endpoint: new RegExp(`iot/v\\d+/equipment/gateways/${gatewaySerial}`),
      json: `/assets/demo/${deviceName}/gateways.json`,
    },
    // get data for buying maintenance kit
    {
      endpoint: new RegExp(
        `equipment/v\\d+/maintenance-packages/${deviceSerial.substring(0, 7)}\\?vkorg=0500`
      ),
      json: `/assets/demo/${deviceName}/maintenance.json`,
    },
    // get installation note
    {
      endpoint: new RegExp(`vitoguide/settings/v\\d+/notes/${installationId}`),
      json: `/assets/demo/${deviceName}/notes.json`,
    },
    {
      endpoint: new RegExp(`iot/v\\d+/smart-notes/installations/${installationId}/smartNotes`),
      json: `/assets/demo/${deviceName}/smart-notes.json`,
    },
    // Remote Service Diagnosis
    {
      endpoint: new RegExp(`iot/v\\d+/analytics-api/dataLake/remoteservice/v0/diagnosis`),
      json: `/assets/demo/${deviceName}/permament-inspection/diagnosis.json`,
    },
    // Remote Service Features
    {
      endpoint: new RegExp(`iot/v\\d+/analytics-api/dataLake/remoteservice/v0/device_features`),
      json: `/assets/demo/${deviceName}/permament-inspection/features.json`,
    },
    // remote diagnostics snapshots?type=snapshots
    {
      endpoint: new RegExp(
        `iot\/v\\d+\/equipment\/installations\/${installationId}\/gateways\/${gatewaySerial}\/snapshots\\?type=snapshot`
      ),
      json: `/assets/demo/remote-diagnostics/snapshots/snapshots.type.snapshots.json`,
    },
    // remote diagnostics snapshots?type=backup
    {
      endpoint: new RegExp(
        `iot\/v\\d+\/equipment\/installations\/${installationId}\/gateways\/${gatewaySerial}\/snapshots\\?type=backup`
      ),
      json: `/assets/demo/remote-diagnostics/backup-and-restore/snapshots.type.backup.json`,
    },
    // remote diagnostics snapshots/configurations/backup
    {
      endpoint: new RegExp(
        `iot\/v\\d+\/equipment\/installations\/${installationId}\/gateways\/${gatewaySerial}\/snapshots\/configurations\/backup`
      ),
      json: `/assets/demo/remote-diagnostics/backup-and-restore/backup.json`,
    },
    // dynamically feature change - remoteDiagnostics {
    {
      endpoint: new RegExp(`mockedAssets/${gatewaySerial}/remoteDiagnosticsActive`),
      json: `/assets/demo/${deviceName}/remote-diagnostics/remote.diagnostics.active.feature.json`,
    },
    {
      endpoint: new RegExp(`mockedAssets/${gatewaySerial}/remoteDiagnosticsReady`),
      json: `/assets/demo/${deviceName}/remote-diagnostics/remote.diagnostics.ready.feature.json`,
    },
  ];

  const mappedEndpoints = endpoint.map((entry) => {
    return {
      ...entry,
      details: {
        deviceName,
        installationId,
        deviceId,
        deviceSerial,
        gatewaySerial,
        srcDeviceList,
      },
    };
  });

  return mappedEndpoints;
};

function getEndpointsForSrcDevices(
  srcDeviceList: string[],
  deviceName: string,
  installationId: number,
  gatewaySerial: string
): MockedEndpoint[] {
  const endpoints = [
    // gateway.devices
    {
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations\/${installationId}\/gateways\/${gatewaySerial}\/features/gateway.devices`
      ),
      json: `/assets/demo/${deviceName}/gateway.devices.json`,
    },
  ];

  // get all installation device gateway features
  srcDeviceList.forEach((srcDeviceId) => {
    endpoints.push({
      endpoint: new RegExp(
        `iot/v\\d+/equipment/installations/${installationId}/gateways/${gatewaySerial}/devices/${srcDeviceId}/features`
      ),
      json: `/assets/demo/${deviceName}/installations.devices.${srcDeviceId}.features.reduced-hypermedia.json`,
    });
  });

  return endpoints;
}

export const activeErrorsEndpoints = (
  deviceName: string,
  deviceSerial: string,
  errorCode: string,
  countryCode = 'de',
  languageCode = 'de'
): MockedEndpoint[] => [
  // get error details from cosimadb
  {
    endpoint: new RegExp(
      `service-documents/v\\d+/error-database\\?materialNumber=${deviceSerial.substring(
        0,
        7
      )}&errorCode=${errorCode}&countryCode=${
        countryCode ? countryCode.toUpperCase() : '\\w+'
      }&languageCode=${languageCode ? languageCode.toLowerCase() : '\\w+'}`
    ),
    json: `/assets/demo/${deviceName}/errors/error.${errorCode}.${countryCode}-${languageCode}.json`,
  },
];

export const dataTrendingEndpoints = (
  deviceName: string,
  gatewaySerial: string,
  _features: string,
  resolution: string
): MockedEndpoint[] => {
  if (
    deviceName === 'vitocal-200' ||
    deviceName === 'vitocal-252-a' ||
    deviceName === 'vitodens-300' ||
    deviceName === 'vitodens-100-opentherm' ||
    deviceName === 'vitovalor' ||
    deviceName === 'vitocharge' ||
    deviceName === 'vitoair-fs' ||
    deviceName === 'vitocal-222-s' ||
    deviceName === 'vitocal-252-mpk'
  ) {
    return [
      {
        endpoint: new RegExp(`iot/v\\d+/analytics-api/dataLake/trending/v0/timeseries`),
        json: `/assets/demo/data.trending/resolution.${resolution}.json`,
        details: {
          deviceName,
          gatewaySerial,
          resolution,
        },
      },
    ];
  }
};

export const dataHemsEndpoints = (
  deviceName: string,
  gatewaySerial: string,
  resolution: string
): MockedEndpoint[] => {
  if (
    deviceName === 'vitocharge' ||
    deviceName === 'vitocharge-vx3' ||
    deviceName === 'vitocal-252-a' ||
    deviceName === 'vitocal-252-mpk'
  ) {
    return [
      {
        endpoint: new RegExp(`iot/v\\d+/analytics-api/dataLake/hems/v0/timeseries`),
        json: `/assets/demo/hems.trending/timeseries.resolution.${resolution}.json`,
        details: {
          deviceName,
          gatewaySerial,
          resolution,
        },
      },
      {
        endpoint: new RegExp(`iot/v\\d+/analytics-api/dataLake/chronos/v0/thermal_energy`),
        json: `/assets/demo/hems.trending/thermal.energy.resolution.${resolution}.json`,
        details: {
          deviceName,
          gatewaySerial,
          resolution,
        },
      },
    ];
  }
};

export const hydraulicSchemesEndpoints = (): MockedEndpoint[] => [
  // hydraulic schemes - schemes
  {
    endpoint: new RegExp('equipment/v3/devices/.+/schemes'),
    json: `/assets/demo/remote-diagnostics/hydraulic-schemes/schemes.json`,
  },
  // hydraulic schemes - svg
  {
    endpoint: new RegExp(
      'equipment/v\\d+/devices/.+/hydraulic-schema\\?hydramatrixcode=16_9_0_0_0_0_0_0_0_0_0_0_0_0_0_0_0_6_1_0_0_0_0_0_2_0_0_16_1_3_0_0_0_0_0_0_0_0_0_0_0_0_0_0_6_0_0_0_0_0_0&.+'
    ),
    json: `/assets/demo/remote-diagnostics/hydraulic-schemes/hydraulic-scheme.txt`,
  },
  // hydraulic schemes - INTERNAL svg
  {
    endpoint: new RegExp(
      'equipment/v\\d+/devices/.+/internal-hydraulic-schema\\?hydramatrixcode=16_9_0_0_0_0_0_0_0_0_0_0_0_0_0_0_0_6_1_0_0_0_0_0_2_0_0_16_1_3_0_0_0_0_0_0_0_0_0_0_0_0_0_0_6_0_0_0_0_0_0&.+'
    ),
    json: `/assets/demo/remote-diagnostics/hydraulic-schemes/internal-hydraulic-scheme.txt`,
  },
];

export const useCasesEndpoints = (useCaseName: string): MockedEndpoint[] => [
  // remote diagnostics - use cases
  {
    endpoint: new RegExp(
      `viessmann-platform.io/dc-diagstack-\\d+/diagnostic-info/available-use-cases/LL_0002_HMU_CAN01?[\\W\\w]+`
    ),
    json: `/assets/demo/remote-diagnostics/use-cases/use-cases.json`,
  },
  // remote diagnostics - use case vdd list
  {
    endpoint: new RegExp(
      `viessmann-platform.io/dc-diagstack-\\d+/diagnostic-info/vdd/LL_0002_HMU_CAN01/${useCaseName}/list?[\\W\\w]+`
    ),
    json: `/assets/demo/remote-diagnostics/use-cases/${useCaseName}-vdd-list.json`,
  },
];

export const gtsEndpoints = (dtc: string): MockedEndpoint[] => [
  // available-sequence
  {
    endpoint: new RegExp(
      `viessmann-platform.io/dc-diagstack-\\d+/diagnostic-generic-otx/available-sequences`
    ),
    json: `/assets/demo/remote-diagnostics/gts/available-sequence.json`,
  },
  // execute
  {
    endpoint: new RegExp(`dc-diagstack-\\d+\/diagnostic-generic-otx\/execute_${dtc}`),
    json: `/assets/demo/remote-diagnostics/gts/execute-${dtc}.txt`,
  },
];

export const errorDatabaseErrors = (): MockedEndpoint[] => [
  // // F.7
  {
    endpoint: new RegExp(
      `service-documents/v\\d+/error-database\\?materialNumber=\\d+&errorCode=F.7&countryCode=\\w+&languageCode=\\w+`
    ),
    json: `/assets/demo/remote-diagnostics/gts/errors/error-database-F7-de.json`,
  },
  // F.517
  {
    endpoint: new RegExp(
      `service-documents/v\\d+/error-database\\?materialNumber=\\d+&errorCode=F.517&countryCode=\\w+&languageCode=\\w+`
    ),
    json: `/assets/demo/remote-diagnostics/gts/errors/error-database-F517-de.json`,
  },
];

export const backupEndpoints = (): MockedEndpoint[] => [
  // vdds - new backup created
  {
    endpoint: new RegExp(`iot\/v1\/equipment\/installations\/\\d+\/gateways\/.+\/snapshots$`),
    json: `/assets/demo/remote-diagnostics/backup-and-restore/new/vdds.json`,
  },
  // backup (id) - new backup created
  {
    endpoint: new RegExp(
      `iot\/v\\d+\/equipment\/installations\/\\d+/gateways/\.+\/snapshots/e17a9a7c-8d0f-4696-b705-fa9949fe78d7$`
    ),
    json: `/assets/demo/remote-diagnostics/backup-and-restore/new/e17a9a7c-8d0f-4696-b705-fa9949fe78d7.json`,
  },
];

export const snapshotEndpoints = (): MockedEndpoint[] => [
  // snapshots - vdds of snapshots
  {
    endpoint: new RegExp(
      new RegExp(`iot\/v\\d+\/equipment\/installations\/\\d+/gateways/\.+/snapshots\/\.+\/vdds`)
    ),
    json: '/assets/demo/remote-diagnostics/snapshots/vdds.json',
  },
  // vdds - new snapshot created
  {
    endpoint: new RegExp(`iot\/v1\/equipment\/installations\/\\d+\/gateways\/.+\/snapshots$`),
    json: `/assets/demo/remote-diagnostics/snapshots/new/vdds.json`,
  },
  // snapshot (id) - new snapshot created
  {
    endpoint: new RegExp(`iot\/v\\d+\/equipment\/installations\/\\d+/gateways/\.+\/snapshots/0$`),
    json: `/assets/demo/remote-diagnostics/snapshots/new/0.json`,
  },
];

export const mockProFunctionalitiesEndpoints = (
  boilerSerial: string,
  deviceName: string
): MockedEndpoint[] => [
  // checklist - equipment/v3/vibooks/checklist
  // if new installation is needed just put boilerSerial in url property in checklist.json
  {
    endpoint: new RegExp(
      `equipment\/v\\d+\/vibooks\/checklist\/${boilerSerial}\\?countryCode=DE&languageCode=de`
    ),
    json: `/assets/demo/${deviceName}/maintenance-checklist/checklist.json`,
  },
];
